import { Controller } from "@hotwired/stimulus"
import { showAiOverlay, hideAiOverlay } from "./ai_overlay"

export default class extends Controller {
  static targets = ["input", "preview", "prompt", "placeholder", "generateButton", "spinner"]
  static values = {
    url: { type: String, default: "/app/ai_images/generate" },
    imageType: String,
    premium: Boolean
  }

  connect() {
    this.generating = false
  }

  async generate(event) {
    event.preventDefault()

    if (this.generating) return
    if (!this.premiumValue) {
      window.location.href = "/app/upgrade"
      return
    }

    const prompt = this.promptTarget.value.trim()
    if (!prompt) {
      this.showError("Please enter a description for the image")
      return
    }

    this.setGenerating(true)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        },
        body: JSON.stringify({
          prompt: prompt,
          image_type: this.imageTypeValue
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.updateImage(data.image_url)
        this.showSuccess("Image generated successfully!")
      } else {
        this.showError(data.error || "Failed to generate image")
      }
    } catch (error) {
      console.error("AI Image generation error:", error)
      this.showError("Failed to generate image. Please try again.")
    } finally {
      this.setGenerating(false)
    }
  }

  updateImage(url) {
    if (this.hasInputTarget) {
      this.inputTarget.value = url
    }

    if (this.hasPreviewTarget) {
      this.previewTarget.src = url
      this.previewTarget.classList.remove("hidden")
    }

    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add("hidden")
    }
  }

  setGenerating(isGenerating) {
    this.generating = isGenerating

    if (isGenerating) {
      showAiOverlay("Generating Image...")
    } else {
      hideAiOverlay()
    }

    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = isGenerating
      if (isGenerating) {
        this.generateButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
      } else {
        this.generateButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
      }
    }

    if (this.hasSpinnerTarget) {
      if (isGenerating) {
        this.spinnerTarget.classList.remove("hidden")
      } else {
        this.spinnerTarget.classList.add("hidden")
      }
    }
  }

  showError(message) {
    // Try to use existing flash system or show alert
    const flashContainer = document.querySelector("[data-controller='flash']")
    if (flashContainer) {
      const flash = document.createElement("div")
      flash.className = "flash-alert"
      flash.textContent = message
      flashContainer.appendChild(flash)
      setTimeout(() => flash.remove(), 5000)
    } else {
      alert(message)
    }
  }

  showSuccess(message) {
    const flashContainer = document.querySelector("[data-controller='flash']")
    if (flashContainer) {
      const flash = document.createElement("div")
      flash.className = "flash-notice"
      flash.textContent = message
      flashContainer.appendChild(flash)
      setTimeout(() => flash.remove(), 5000)
    }
  }

  getCSRFToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }
}
